import { Box, Stack } from "@mui/material";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import SwipeableViews from "react-swipeable-views";

interface ProgressCarouselContextValue {
  currentIndex: number;
  totalSlides: number;
  goToNext: () => void;
  goToPrevious: () => void;
  goToSlide: (index: number) => void;
}

const ProgressCarouselContext = createContext<ProgressCarouselContextValue | undefined>(undefined);

export function useProgressCarousel(): ProgressCarouselContextValue {
  const context = useContext(ProgressCarouselContext);
  if (!context) {
    throw new Error("useProgressCarousel must be used within a ProgressCarousel");
  }

  return context;
}

interface ProgressCarouselProps {
  children?: ReactNode[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  totalCount?: number;
}

export function ProgressCarousel(props: ProgressCarouselProps) {
  const { children, currentIndex, onIndexChange, totalCount } = props;

  const renderedChildren = children ?? [];
  if (totalCount && (!children || children.length === 0)) {
    renderedChildren.push(
      ...Array.from({ length: totalCount }, (_, index) => (
        <Box key={index}>
          <Box />
        </Box>
      ))
    );
  }

  const totalSlides = renderedChildren.length;

  const progressBarRefs = useRef<Array<HTMLDivElement | undefined>>([]);
  const currentIndexRef = useRef(currentIndex);
  const isSwipingRef = useRef(false);

  function updateProgressBars(
    // in the range [-1, 1] where -1 is swiping left, and 1 is swiping right
    swipeProgress: number
  ) {
    const currentIndex = currentIndexRef.current;

    for (let barIndex = 0; barIndex < progressBarRefs.current.length; barIndex += 1) {
      const barRef = progressBarRefs.current[barIndex];

      if (!barRef) {
        continue;
      }

      let width = "0%";

      if (barIndex < currentIndex) {
        // Completed slides
        width = "100%";
      } else if (barIndex === currentIndex) {
        // Current slide
        width =
          swipeProgress < 0
            ? // Swiping left: reduce from 100% to 0%
              `${100 + swipeProgress * 100}%`
            : // Swiping right: stay at 100%
              "100%";
      } else if (barIndex === currentIndex + 1) {
        // Next slide
        width =
          swipeProgress >= 0
            ? // Swiping right: increase from 0% to 100%
              `${swipeProgress * 100}%`
            : // Swiping left: stay at 0%
              "0%";
      }

      barRef.style.width = width;
      barRef.style.transition = isSwipingRef.current ? "none" : "";
    }
  }

  // Update current index ref and reset progress bars
  useEffect(() => {
    const previousIndex = currentIndexRef.current;
    currentIndexRef.current = currentIndex;

    // If Large jump - set intermediate bars' width instantly, making sure only the target is animated
    // Otherwise we have multiple bars animating in unison (not consecutively), which looks terrible
    if (Math.abs(currentIndex - previousIndex) > 1) {
      for (let barIndex = 0; barIndex < progressBarRefs.current.length; barIndex += 1) {
        const barRef = progressBarRefs.current[barIndex];
        if (!barRef) {
          continue;
        }

        if (currentIndex > previousIndex && barIndex > previousIndex && barIndex < currentIndex) {
          // Forward jump: instantly fill bars between prevIndex and currentIndex-1
          // This ensures currentIndex fills with animation
          barRef.style.transition = "none";
          barRef.style.width = "100%";
          // Force reflow to ensure instant change takes effect
          void barRef.offsetHeight;
          barRef.style.transition = "";
        } else if (barIndex > currentIndex + 1 && barIndex <= previousIndex) {
          // Backward jump: instantly empty bars between currentIndex+2 and prevIndex
          // This ensures currentIndex+1 empties with animation
          barRef.style.transition = "none";
          barRef.style.width = "0%";
          // Force reflow to ensure instant change takes effect
          void barRef.offsetHeight;
          barRef.style.transition = "";
        }
      }
    }

    // Update all bars to their correct state (this will animate the target bar)
    updateProgressBars(0);
  }, [currentIndex]);

  const onSwitching = useCallback(
    (
      // this is the swipe progress that comes in from react-swipeable-views's onSwitching callback
      // it's in the range [0, n-1], where n is the number of slides
      rawSwipeProgress: number
    ) => {
      // Detect if user is actively swiping or has let go
      // When the user stops swiping, react-swipeable-views calls onSwitching with a whole number
      // which matches the index we're snapping to
      //
      // On the other hand, WHILE swiping, we get fractional numbers between [0, number_of_slides - 1]
      isSwipingRef.current = !Number.isInteger(rawSwipeProgress);

      // Convert to -1 to 1 range
      updateProgressBars(rawSwipeProgress - currentIndexRef.current);
    },
    []
  );

  const contextValue: ProgressCarouselContextValue = useMemo(
    () => ({
      currentIndex,
      totalSlides,
      goToNext: () => {
        if (currentIndex < totalSlides - 1) {
          onIndexChange(currentIndex + 1);
        }
      },
      goToPrevious: () => {
        if (currentIndex > 0) {
          onIndexChange(currentIndex - 1);
        }
      },
      goToSlide: (index: number) => {
        if (index >= 0 && index < totalSlides) {
          onIndexChange(index);
        }
      },
    }),
    [currentIndex, onIndexChange, totalSlides]
  );

  return (
    <ProgressCarouselContext.Provider value={contextValue}>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Progress bars container */}
        <Box
          sx={(theme) => ({
            height: theme.spacing(9),
            flexShrink: 0,
            paddingY: theme.spacing(2),
            paddingX: theme.spacing(5),
            display: "flex",
            alignItems: "center",
          })}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              width: "100%",
              height: "2px",
            }}
          >
            {renderedChildren?.map((_, index) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                data-testid="progress-bar-container"
                sx={(theme) => ({
                  position: "relative",
                  flex: 1,
                  height: "2px",
                  backgroundColor: theme.palette.background.secondary,
                  borderRadius: "1px",
                  overflow: "hidden",
                })}
              >
                <Box
                  ref={(element: HTMLDivElement) => {
                    progressBarRefs.current[index] = element;
                  }}
                  sx={(theme) => ({
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: index <= currentIndex ? "100%" : "0%",
                    backgroundColor: theme.palette.background.invert,
                    borderRadius: "2px",
                    transition: theme.transitions.create("width", {
                      duration: theme.transitions.duration.standard,
                      easing: theme.transitions.easing.easeInOut,
                    }),
                  })}
                />
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Swipeable content */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
          }}
        >
          <SwipeableViews
            enableMouseEvents
            index={currentIndex}
            style={{
              height: "100%",
            }}
            containerStyle={{
              height: "100%",
            }}
            onChangeIndex={(index) => {
              currentIndexRef.current = index;
              onIndexChange(index);
            }}
            onSwitching={onSwitching}
          >
            {renderedChildren}
          </SwipeableViews>
        </Box>
      </Box>
    </ProgressCarouselContext.Provider>
  );
}
